import { ColumnPassThroughMethodOptions } from "primereact/column";
import { classNames } from "primereact/utils";
import {
  DataTablePassThroughMethodOptions,
  DataTablePassThroughOptions,
  DataTableValueArray,
} from "primereact/datatable";
import { CheckboxPassThroughMethodOptions } from "primereact/checkbox";

import { paginator } from "./datatable.paginator";

export const datatable: Partial<DataTablePassThroughOptions> = {
  paginator: paginator,
  root: ({ props }: DataTablePassThroughMethodOptions<DataTableValueArray>) => ({
    className: classNames("relative", {
      "flex flex-col h-full min-h-[1px]": props.scrollable && props.scrollHeight === "flex",
    }),
  }),
  loadingOverlay: {
    className: classNames(
      "fixed w-full h-full t-0 l-0 bg-gray-100/40",
      "transition duration-200",
      "absolute flex items-center justify-center z-10",
      "dark:bg-gray-950/40", // Dark Mode
    ),
  },
  loadingIcon: () => "w-8 h-8",
  wrapper: ({ props }: DataTablePassThroughMethodOptions<DataTableValueArray>) => ({
    className: classNames("scroll", "border border-gray-300", {
      relative: props.scrollable,
      "flex flex-col": props.scrollable && props.scrollHeight === "flex",
    }),
  }),
  header: ({ props }: DataTablePassThroughMethodOptions<DataTableValueArray>) => ({
    className: classNames(
      "text-slate-700 border-gray-300 font-bold p-4 ",
      "dark:border-blue-900/40 dark:text-white/80 dark:bg-gray-900", // Dark Mode
      props.showGridlines ? "border-x border-t border-b-0" : "border-x-0",
    ),
  }),
  thead: () => ({
    className: "top-0 sticky z-[1]  bg-urbe-primary200 h-[38px]",
  }),
  column: {
    root: ({ props }: ColumnPassThroughMethodOptions) => {
      return {
        className: classNames(props?.className),
      };
    },
    rowCheckbox: {
      root: ({ props }: CheckboxPassThroughMethodOptions) => {
        return {
          className: classNames("justify-center", props.className),
        };
      },
    },
    columnResizer: () =>
      "block absolute top-0 right-0 m-0 w-2 h-full p-0 cursor-col-resize border border-transparent",
    headerCell: ({ props, context }: ColumnPassThroughMethodOptions) => {
      return {
        className: classNames(
          "border-y-0 dark:border-blue-900/40 text-left",
          "p-2 font-semibold text-sm leading-[18px] h-[38px]",
          "transition duration-200",
          "text-rdc-primary",
          "bg-primary-100",
          "dark:text-white/80 dark:bg-blue-30", //dark
          {
            "p-1": context.size === "small",
            "p-5": context.size === "large",
            "cursor-pointer": props.sortable,
            "sticky z-[1]": props.frozen, // Frozen Columns
            "overflow-hidden space-nowrap bg-clip-padding": context.resizable, // Resizable
          },
          props.className,
        ),
      };
    },
    filterMenuButton: ({ context }: ColumnPassThroughMethodOptions) => ({
      className: classNames(
        "inline-flex justify-center items-center cursor-pointer no-underline overflow-hidden relative ml-2",
        "w-8 h-8 rounded-[50%]",
        "transition duration-200",
        "hover:text-slate-700 hover:bg-gray-300/20", // Hover
        "focus:outline-0 focus:outline-offset-0 focus:shadow-[0_0_0_0.2rem_rgba(191,219,254,1)]", // Focus
        "dark:text-white/70 dark:hover:text-white/80 dark:bg-gray-900", // Dark Mode
        {
          "text-blue-400": context.active,
        },
      ),
    }),
    bodyCell: ({ props, context }: ColumnPassThroughMethodOptions) => {
      return {
        className: classNames(
          "py-0 px-2",
          "text-left border-0 border-b border-solid border-gray-100 text-sm font-medium",
          "dark:text-white/80 dark:border-blue-900/40", // Dark Mode
          {
            "py-0 px-1 text-[0.75rem]": context.size === "small",
            "py-0 px-2": context.size === "normal",
            "p-5": context.size === "large",
            sticky: props?.frozen === true, // Frozen Columns
            "border-x border-y": context.showGridlines,
          },
          props?.className,
        ),
      };
    },
    sortIcon: ({ context }: ColumnPassThroughMethodOptions) => ({
      className: classNames("pl-1", { hidden: context.sorted === false }),
    }),
  },

  bodyRow: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { context, props }: any, // PrimeReact type mistake, context.seelect isn't in the DataTablePassThroughMethodOptions type
  ) => {
    return {
      className: classNames(
        context.selected
          ? "bg-rdc-primaryLight-50 text-rdc-primary dark:bg-blue-300"
          : "bg-white text-gray-600 dark:bg-gray-900",
        context.stripedRows
          ? context.index % 2 === 0
            ? "bg-white text-gray-600 dark:bg-gray-900"
            : "bg-blue-50/50 text-gray-600 dark:bg-gray-950"
          : "",
        {
          "h-[35px]": props.size === "small",
          "h-[49px]": props.size !== "small",
        },
        "transition duration-200",
        "bg-white",
        "hover:bg-rdc-primaryLight", // Hover
        "focus:outline focus:outline-[0.15rem] focus:outline-rdc-primaryLight focus:outline-offset-[-0.15rem]", // Focus
        "dark:text-white/80 dark:focus:outline dark:focus:outline-[0.15rem] dark:focus:outline-rdc-primaryLight dark:focus:outline-offset-[-0.15rem]", // Dark Mode
        {
          "cursor-pointer": context.selectable,
        },
      ),
    };
  },
  resizeHelper: () => "absolute hidden w-px z-10 bg-blue-500 dark:bg-blue-300",
};
